import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { kebabCase } from 'es-toolkit';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessPartnerQuery, PartnerAtEstablishmentType } from '@/__generated__/businessPartnerQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertBusinessPartner } from '@/graphql/mutation/survey/business';
import { partnerQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';
import { hasEmployeeAtom } from '@/lib/stores/survey/common';

const answers: { value: PartnerAtEstablishmentType; label: string }[] = [
  { value: 'yes', label: '네, 공동사업자로 시작했어요' },
  { value: 'joinMidway', label: '중간에 공동사업자로 참여했어요' },
  { value: 'no', label: '공동사업자가 아니에요' }
];

export const PartnerContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessPartnerQuery> }) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const [upsertBusinessAnswer, isLoading] = useUpsertBusinessAnswer(upsertBusinessPartner);
  const hasEmployee = useAtomValue(hasEmployeeAtom);
  const { getSurveyBusinessAnswer, getSurveyBusinessInfo } = usePreloadedQuery<businessPartnerQuery>(partnerQuery, queryRef);
  const [selectedAnswer, setSelectedAnswer] = useState<PartnerAtEstablishmentType>('blank');

  const business = getSurveyBusinessAnswer.businessAnswer?.[0];

  useEffect(() => {
    if (business) {
      setSelectedAnswer(business?.hasPartnerAtTheTimeOfEstablishment || 'blank');
    }
  }, [setSelectedAnswer, business]);

  const openingDate = ofbDtToYYMMDD(getSurveyBusinessInfo?.business?.[0]?.ofbDt || '');

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_partner_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <ProgressBar step={1} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_partner_confirm', eventAttributes);

            upsertBusinessAnswer(
              { hasPartnerAtTheTimeOfEstablishment: selectedAnswer },
              {
                onCompleted: () => {
                  if (hasEmployee) {
                    router.push(SURVEY_PAGES.승계직원);
                    return;
                  }
                  router.push(SURVEY_PAGES.사업장_근무여부);
                }
              }
            );
          }}
        >
          <article>
            <section>
              <h1 className={common.content_title}>
                창업일인 {openingDate.yy}년 {openingDate.mm}월 {openingDate.dd}일부터 공동사업자셨나요?
              </h1>

              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_partner_${kebabCase(value)}`, eventAttributes);

                  setSelectedAnswer(value);
                }}
              />
            </section>
            <section className={'pb-5 pt-6'}>
              <h2 className={common.section_title}>공동사업자, 왜 확인이 필요한가요?</h2>
              <p className={common.section_list_item}>최초로 창업할 때 같이 시작한 공동사업자 모두 환급받을 수 있기 때문에 확인이 필요해요.</p>
            </section>
          </article>

          {selectedAnswer !== 'blank' && (
            <CTA type={'submit'} disabled={isLoading}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};

// ofbDate = 창업일
export const ofbDtToYYMMDD = (ofbDt: string) => {
  if (ofbDt.length !== 8) {
    return {
      yy: '',
      mm: '',
      dd: ''
    };
  }
  return {
    yy: ofbDt.slice(2, 4),
    mm: Number(ofbDt.slice(4, 6)).toString(),
    dd: Number(ofbDt.slice(-2)).toString()
  };
};
